import React, { useContext, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { Context } from "../contexts/AppContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { timeStringToSeconds } from "../../lib/common";
import { postData } from "../../lib/utils";

function ChapterListModal() {
  const { value } = useContext(Context);
  const [appState, setAppState] = value;

  const [modalOpen, setModalOpen] = useState(appState.modalData);
  const [isVideoPlaying, setVideoPlaying] = useState(false);

  const videoRef = useRef(null);
  const [hasCountedView, setHasCountedView] = useState(false);
  const playTimerRef = useRef(null);


  useEffect(() => {
  const handlePopState = () => {
    if (isVideoPlaying) {
      setVideoPlaying(false);
      return;
    }

    closeModal();
  };

  window.addEventListener("popstate", handlePopState);

  return () => window.removeEventListener("popstate", handlePopState);
}, [isVideoPlaying]);

  useEffect(() => {
  const handlePopState = (e) => {
    const state = e.state;

    // if browser back pressed
    if (!state?.type || state.type !== "videoPlayer") {
      closeModal();
    }
  };

  window.addEventListener("popstate", handlePopState);

  return () => {
    window.removeEventListener("popstate", handlePopState);
  };
  }, []);
  

  useEffect(() => {
    setModalOpen(appState.modalData);
  }, [appState.modalData]);

  Modal.setAppElement("#root");

  const customStyles = {
    content: {
      position: 'fixed', // recommended over 'absolute' for full-screen modals
      top: '0',
      left:'auto',
      right: '0',
  
      width: isVideoPlaying ? '100%' : '300px',  // use width instead of maxWidth for fixed side panel
      height: '100vh', // full screen height
  
      overflow: 'auto', // allow scroll if content overflows
      border: '1px solid #00bcd4',
      backgroundColor: '#fff', // ensure visibility
      padding: isVideoPlaying ? '0px':'20px',
      zIndex: 1000, // just in case
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 999,
    },
  };

  const closeModal = () => {
  setAppState((prev) => ({
    ...prev,
    modalData: { ...prev.modalData, isOpen: false },
  }));

  // important: sync browser back
  if (window.history.state?.type === "videoPlayer") {
    window.history.back();
  }
};
  const playVideoHandler = (evt) => {
    const cuepoint = timeStringToSeconds(
      evt.currentTarget.dataset.cuepoint
    );

    const videoElement = videoRef.current;

    videoElement.currentTime = cuepoint;
    setVideoPlaying(true);
    videoElement.play();
  };

 const handleBack = () => {
  videoRef.current.pause();
  videoRef.current.currentTime = 0;
  setVideoPlaying(false);

  // IMPORTANT: go back to chapter list state
  window.history.back();
};

  useEffect(() => {
    if (appState.currentChapterArr.length <= 0) {
      setVideoPlaying(true);
    }
  }, [appState.currentChapterArr]);

  const handleVideoPlay = () => {
    if (!hasCountedView) {
      playTimerRef.current = setTimeout(() => {
        sendViewCount();
        setHasCountedView(true);
      }, 3000);
    }
  };

  const handleVideoPause = () => {
    clearTimeout(playTimerRef.current);
  };

  const sendViewCount = async () => {
    const videoId = appState.currentVideoId;
    if (!videoId) return;

    await postData(`videos/updateView/${videoId}`);
  };

  const handleVideoEnd = () => {
    if (appState.modalData.isPlayAll) {
      playNextVideo();
    }
  };

  const playNextVideo = () => {
    const currentIndex = appState.videoArr.findIndex(
      (v) => v.url === appState.currentVideoUrl
    );

    const nextIndex = currentIndex + 1;

    if (nextIndex < appState.videoArr.length) {
      setAppState((prev) => ({
        ...prev,
        currentVideoUrl: prev.videoArr[nextIndex].url,
      }));
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play();
    }
  }, [appState.currentVideoUrl]);

  return (
    <Modal
      isOpen={modalOpen.isOpen}
      onRequestClose={closeModal}
      style={customStyles}
    >
      <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden">

        {/* 🎬 VIDEO */}
        <video
          className={`w-full h-full object-contain ${
            isVideoPlaying ? "block" : "hidden"
          }`}
          controls
          ref={videoRef}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onEnded={handleVideoEnd}
        >
          <source
            src={`${process.env.REACT_APP_VIDEO_DEST_BASE_PATH}${appState.currentVideoUrl}`}
            type="video/mp4"
          />
        </video>

        {/* 🔙 BACK BUTTON */}
        {isVideoPlaying && (
          <button
            onClick={handleBack}
            className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded hover:bg-gray-700"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}

        {/* 📚 CHAPTER PANEL */}
        {!isVideoPlaying && appState.currentChapterArr.length > 0 && (
         <div
  className="
    fixed top-0 right-0 bottom-0
    w-full sm:w-[360px]
    bg-gradient-to-b from-[#0b0b0b] to-black
    text-white flex flex-col
    overflow-y-auto
    shadow-2xl
    border-l border-white/10
    z-50
  "
>
  {/* HEADER */}
  <div className="sticky top-0 z-10 bg-black/80 backdrop-blur-md border-b border-white/10">
    <div className="flex justify-between items-center px-4 py-4">
      <h2 className="text-lg font-semibold tracking-wide">
        📚 Chapters
      </h2>

      <button
        onClick={closeModal}
        className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-red-500/80 transition"
      >
        ❌
      </button>
    </div>
  </div>

  {/* CONTENT */}
  <div className="p-3 space-y-3">

    {/* PLAY FROM START */}
    <button
      data-cuepoint="00:00:00"
      onClick={playVideoHandler}
      className="
        w-full text-left
        px-4 py-3
        rounded-xl
        bg-white/5 hover:bg-white/10
        border border-white/10
        transition
        flex items-center gap-2
      "
    >
      <span className="text-green-400">▶</span>
      <span className="font-medium">Play from Beginning</span>
    </button>

    {/* CHAPTER LIST */}
    {appState.currentChapterArr.map((item, index) => (
      <button
        key={index}
        data-cuepoint={item.cuePoint}
        onClick={playVideoHandler}
        className="
          w-full text-left
          px-4 py-3
          rounded-xl
          bg-white/5 hover:bg-white/10
          border border-white/10
          transition
          group
        "
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium group-hover:text-yellow-300">
            ▶ {item.title}
          </span>

          <span className="text-xs text-white/40">
            {item.cuePoint}
          </span>
        </div>
      </button>
    ))}

  </div>
</div>
        )}
      </div>
    </Modal>
  );
}

export default ChapterListModal;